import { Observable } from 'rxjs';

import {
	IDiscussAddReplyResponseData,
	IDiscussApi,
	IDiscussPostData,
	IDiscussRepliesResponseData,
	IDiscussSearchParams,
	DiscussSearchResponseDataType,
	IDiscussVoteResponseData,
	IDiscussToggleAcceptedAnswerResponseData,
	IDiscussDetailsResponseDataType,
	IPostQuestionPayload,
	IGetTagsResponse,
} from '../private/discuss-api/discuss-api.interface';
import {
	AxiosInstanceType,
	SlAxiosInstanceService,
} from '../private/services/sl-axios-instance.service';
import { IApiUrls } from '../private/global.interface';
import { AuthLevel, Authorize } from '../private/authorize.decorator';
import { SlApiContext } from '../private/api-context';
import { constructUrl } from '../private/utils/httpParamsUtils';
import { Service, Container } from '../../symphony';

@Service()
export class DiscussApi implements IDiscussApi {
	private environmentUrl: string;

	private trendsApiHost: string;

	private axiosInstance: AxiosInstanceType;

	constructor() {
		this.environmentUrl = (
            Container.take('global', 'envUrl') as IApiUrls
		)?.monolithApiHost;

		this.trendsApiHost = (
            Container.take('global', 'envUrl') as IApiUrls
		)?.trendsApiHost;

		this.axiosInstance = Container.take(
			SlApiContext,
			SlAxiosInstanceService,
		).axios;
	}

    @Authorize(AuthLevel.public)
	public getQuestion(questionId: number): Observable<IDiscussPostData> {
		return this.axiosInstance.post(
			constructUrl(this.environmentUrl, 'discussion/GetPost'),
			{
				id: questionId,
			},
		);
	}

    @Authorize(AuthLevel.authenticated)
    public deletePost(questionId: number): Observable<object> {
    	return this.axiosInstance.post(
    		constructUrl(this.environmentUrl, 'discussion/DeletePost'),
    		{
    			id: questionId,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public editPost(
    	id: number,
    	message: string,
    	tags: string[],
    	title: string,
    ): Observable<IDiscussPostData> {
    	return this.axiosInstance.post(
    		constructUrl(this.environmentUrl, 'discussion/EditPost'),
    		{
    			id,
    			title,
    			message,
    			tags,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public votePost(
    	questionId: number,
    	vote: number,
    ): Observable<IDiscussVoteResponseData> {
    	return this.axiosInstance.post(
    		constructUrl(this.environmentUrl, 'discussion/VotePost'),
    		{
    			id: questionId,
    			vote,
    		},
    	);
    }

    @Authorize(AuthLevel.public)
    public getReplies(
    	questionId: number,
    	index: number,
    	count: number,
    	orderBy: number,
    ): Observable<IDiscussRepliesResponseData> {
    	return this.axiosInstance.post(
    		constructUrl(this.environmentUrl, 'discussion/GetReplies'),
    		{
    			postId: questionId,
    			index,
    			count,
    			orderBy,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public createReply(
    	questionId: number,
    	replyContent: string,
    ): Observable<IDiscussAddReplyResponseData> {
    	return this.axiosInstance.post(
    		constructUrl(this.environmentUrl, 'discussion/CreateReply'),
    		{
    			postId: questionId,
    			message: replyContent,
    		},
    	);
    }

    @Authorize(AuthLevel.authenticated)
    public postQuestion(
    	payload: IPostQuestionPayload,
    ): Observable<IDiscussAddReplyResponseData> {
    	return this.axiosInstance.post(
    		constructUrl(this.environmentUrl, 'discussion/CreatePost'),
    		payload,
    	);
    }

    public getTags(tag: string): Observable<IGetTagsResponse> {
    	return this.axiosInstance.post(
    		constructUrl(
    			this.environmentUrl,
    			`discussion/GetTags?query=${tag}`,
    		),
    		{},
    	);
    }

    @Authorize(AuthLevel.public)
    public discussSearch({
    	query,
    	filter,
    	index,
    	count,
    	profileId = 0,
    }: IDiscussSearchParams): Observable<DiscussSearchResponseDataType> {
    	return this.axiosInstance.get(
    		constructUrl(this.trendsApiHost, 'discussions/search'),
    		{
    			params: {
    				query,
    				filter,
    				index,
    				count,
    				profileId,
    			},
    		},
    	);
    }

    @Authorize(AuthLevel.public)
    public getDiscussDetails({
    	query,
    	filter,
    	index,
    	count,
    	profileId = 0,
    }: IDiscussSearchParams): Observable<IDiscussDetailsResponseDataType> {
    	return this.axiosInstance.get(
    		constructUrl(this.trendsApiHost, 'discussions/detailed'),
    		{
    			params: {
    				query,
    				filter,
    				index,
    				count,
    				profileId,
    			},
    		},
    	);
    }

    @Authorize(AuthLevel.public)
    public toggleAcceptedAnswer(
    	id: number,
    	accepted: boolean,
    ): Observable<IDiscussToggleAcceptedAnswerResponseData> {
    	return this.axiosInstance.post(
    		constructUrl(
    			this.environmentUrl,
    			'discussion/ToggleAcceptedAnswer',
    		),
    		{
    			id,
    			accepted,
    		},
    	);
    }
}

export * from '../private/discuss-api/discuss-api.interface';
